import React, { useMemo } from 'react';

import Table from '@amzn/awsui-components-console/table';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useNavigate } from 'react-router';

import PaginationControl from 'src/common/ui/PaginationControl/PaginationControl';
import { ActiveTranslationJob } from '../types';
import { TRACK_STRINGS } from '../constants';
import { createActiveColumnDefinitions } from './columns';

interface ActiveJobsTableProps {
  items: ActiveTranslationJob[];
  loading: boolean;
  currentPageIndex: number;
  pagesCount: number;
  hasMore: boolean;
  onPageChange: (pageIndex: number) => void;
}

const ActiveJobsTable: React.FC<ActiveJobsTableProps> = ({
  items,
  loading,
  currentPageIndex,
  pagesCount,
  hasMore,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const columnDefinitions = useMemo(
    () => createActiveColumnDefinitions(navigate),
    [navigate],
  );

  return (
    <Table<ActiveTranslationJob>
      variant="container"
      trackBy="trackingId"
      ariaLabels={{
        tableLabel: 'Active translation jobs',
      }}
      stickyHeader
      stickyColumns={{ first: 1 }}
      stripedRows
      resizableColumns
      loading={loading}
      loadingText={TRACK_STRINGS.ACTIVE_JOBS_LOADING}
      skeleton={loading ? { totalRows: 5 } : undefined}
      header={
        <Header variant="h2">
          {TRACK_STRINGS.ACTIVE_JOBS_HEADER}{' '}
          <Box
            variant="span"
            fontSize="body-s"
            fontWeight="normal"
            color="text-body-secondary"
          >
            {TRACK_STRINGS.ACTIVE_JOBS_SUBTITLE}
          </Box>
        </Header>
      }
      pagination={
        items.length > 0 ? (
          <PaginationControl
            currentPageIndex={currentPageIndex}
            pagesCount={pagesCount}
            openEnd={hasMore}
            onPageChange={onPageChange}
          />
        ) : undefined
      }
      columnDefinitions={columnDefinitions}
      items={items}
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <Box variant="strong">{TRACK_STRINGS.ACTIVE_JOBS_EMPTY_TITLE}</Box>
            <Box variant="p" color="inherit">
              {TRACK_STRINGS.ACTIVE_JOBS_EMPTY_BODY}
            </Box>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default ActiveJobsTable;
